import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { flatMap, map, publishReplay, refCount, switchAll, tap } from 'rxjs/operators';
import { deserialize, deserializeArray } from 'santee-dcts';
import { Settings } from '../core';
import { Category, defaultReportSort, Report, ReportsPackage } from '../documents';
import { getReportType } from '../documents/document-type-resolver';
import { ReportsReadModel } from '../documents/models/reports-read-model';
import { SponsorStudy } from './../sponsor-dashboard/sponsor-study';
import { SponsorUnreadReportsStorage } from './sponsor-unread-reports-storage';
import { FilterFieldValue } from '../covance-filter';
import { prepareFilterUrlParams } from '../documents/prepare-filter-url-params';

@Injectable()
export class SponsorReportsStorage {
    constructor(
        private settings: Settings,
        private http: HttpClient,
        private unreadReportsStorage: SponsorUnreadReportsStorage,
    ) { }

    public loadStudyReports(study$: Observable<SponsorStudy>, category$: Observable<Category>, filterParameters: FilterFieldValue[] = null): Observable<Report[]> {
        let reports = combineLatest(study$, category$)
            .pipe(
                map(([study, category]) => this.getStudyReports(study, category, filterParameters)),
                flatMap((x: Observable<ReportsPackage>) => {
                    return of(of(null), x);
                }),
                switchAll(),
                tap(r => this.unreadReportsStorage.updateUnreads(r)),
                map(r => this.processNewReportsPackage(r)),
                publishReplay(1),
                refCount()
            );

        return reports;
    }

    public loadSiteReports(study$: Observable<SponsorStudy>, siteId$: Observable<string>, category$: Observable<Category>): Observable<Report[]> {
        let reports = combineLatest(study$, siteId$, category$)
            .pipe(
                map(([study, siteId, category]) => this.getSiteReports(study, siteId, category)),
                flatMap((x: Observable<ReportsPackage>) => {
                    return of(of(null), x);
                }),
                switchAll(),
                tap(r => this.unreadReportsStorage.updateUnreads(r)),
                map(r => this.processNewReportsPackage(r)),
                publishReplay(1),
                refCount()
            );

        return reports;
    }

    private getReportsForCategory(response: Object, category: Category): ReportsPackage {
        const readModel = deserialize(response, ReportsReadModel);

        const reportType = getReportType(category.categoryId);
        const reports = deserializeArray(readModel.reports, reportType);

        reports.forEach((x: Report) => {
            x.category = category;
            x.site = null;
        });
        return {
            category: category,
            reports: reports || []
        };
    }


    private processNewReportsPackage(reportsPackage: ReportsPackage) {
        if (!reportsPackage) {
            return null;
        }
        let reps = reportsPackage.reports;
        reps = reps.sort((a, b) => defaultReportSort(a, b));
        return reps;
    }

    private getStudyReports(study: SponsorStudy, category: Category, filterParameters: FilterFieldValue[]): Observable<ReportsPackage> {
        let filtersParams = prepareFilterUrlParams(filterParameters);
        if (filtersParams && category.isLabReport()) {
            filtersParams = filtersParams.append('requestLimit', this.settings.maxNumberOfSponsorLabReports.toString());
        } else
        if (filtersParams) {
            filtersParams = filtersParams.append('requestLimit', this.settings.sqlRequestLimitForActivityReports.toString());
        }

        return this.http.get(
            `${this.settings.apiUrl}/sponsor/studies/${study.protocolId}/documents/${category.categoryId}`,
            { params: filtersParams }
        ).pipe(map((response) => this.getReportsForCategory(response, category)));
    }

    private getSiteReports(study: SponsorStudy, siteId: string, category: Category): Observable<ReportsPackage> {
        return this.http.get(
            `${this.settings.apiUrl}/sponsor/studies/${study.protocolId}/sites/${siteId}/documents/${category.categoryId}`,
        )
            .pipe(map((response) => this.getReportsForCategory(response, category)));
    }
}

